import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { RiRobot2Fill } from 'react-icons/ri';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services", dropdown: true, items: [
      { name: "Portfolio Websites", path: "/services/portfolio" },
      { name: "Business Websites", path: "/services/business" },
      { name: "E-Commerce Stores", path: "/services/ecommerce" },
      { name: "Personal Brand Sites", path: "/services/personal-brand" },
      { name: "Booking/Appointment Sites", path: "/services/booking" },
      { name: "Educational Platforms", path: "/services/educational" },
      { name: "Landing Pages", path: "/services/landing" },
      { name: "Event & Wedding Sites", path: "/services/event" },
      { name: "NGO & Donation Platforms", path: "/services/ngo" },
    ]},
    { name: "Pricing", path: "/pricing" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-cyber-dark/90 backdrop-blur-md' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <motion.div 
            className="flex-shrink-0 flex items-center"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center">
              <RiRobot2Fill className="h-10 w-10 text-cyber-pink animate-neon-pulse" />
              <span className="ml-2 text-xl font-orbitron font-bold relative neon-text" data-text="CyberNest">
                <span className="text-white">Cyber<span className="text-cyber-pink">Nest</span></span>
              </span>
            </Link>
          </motion.div>

          {/* Desktop nav */}
          <motion.div 
            className="hidden md:flex space-x-4 items-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {navItems.map((item, index) => 
              item.dropdown ? (
                <div key={index} className="relative group" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                  <Link 
                    to={item.path}
                    className="group text-white hover:text-cyber-pink font-rajdhani font-medium px-3 py-2 flex items-center"
                  >
                    {item.name}
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </Link>
                  {servicesOpen && (
                    <div className="absolute left-0 mt-2 w-64 origin-top-left bg-cyber-gray/90 backdrop-blur-lg border border-cyber-purple/30 rounded-md shadow-lg ring-1 ring-cyber-purple ring-opacity-20 flex flex-col divide-y divide-cyber-purple/20">
                      {item.items.map((subItem, subIndex) => (
                        <Link 
                          key={subIndex} 
                          to={subItem.path}
                          className="px-4 py-3 text-sm text-white hover:bg-cyber-purple/20 hover:text-cyber-neon transition-colors duration-150"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                  key={index}
                  to={item.path}
                  className="text-white hover:text-cyber-pink font-rajdhani font-medium px-3 py-2 hover:scale-105 transition-all duration-200"
                >
                  {item.name}
                </Link>
              )
            )}
            <Link to="/contact" className="cyber-button ml-4">
              <span>Start Project</span>
            </Link>
          </motion.div>

          {/* Mobile menu button */}
          <motion.div 
            className="md:hidden flex items-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="p-2 rounded-md text-cyber-pink focus:outline-none"
            >
              {isOpen ? (
                <FaTimes className="h-6 w-6" />
              ) : (
                <FaBars className="h-6 w-6" />
              )}
            </button>
          </motion.div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <motion.div 
          className="md:hidden bg-cyber-dark/95 backdrop-blur-lg border-t border-cyber-purple/30"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item, index) => 
              item.dropdown ? (
                <div key={index}>
                  <button 
                    onClick={() => setServicesOpen(!servicesOpen)} 
                    className="w-full text-left text-white hover:bg-cyber-purple/20 block px-3 py-2 rounded-md font-medium"
                  >
                    <div className="flex justify-between items-center">
                      {item.name}
                      <svg className={`w-4 h-4 transform ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </button>
                  {servicesOpen && (
                    <div className="ml-4 border-l border-cyber-purple/30 pl-4 mt-1 space-y-1">
                      {item.items.map((subItem, subIndex) => (
                        <Link 
                          key={subIndex} 
                          to={subItem.path}
                          className="block px-3 py-2 rounded-md text-sm text-white hover:bg-cyber-purple/20 hover:text-cyber-neon"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                  key={index}
                  to={item.path}
                  className="text-white hover:bg-cyber-purple/20 hover:text-cyber-neon block px-3 py-2 rounded-md font-medium"
                >
                  {item.name}
                </Link>
              )
            )}
            <div className="mt-4 px-3 py-2">
              <Link to="/contact" className="cyber-button w-full text-center">
                <span>Start Project</span>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar; 